import React, { useState, useEffect } from 'react';
import FormModal from '../common/FormModal';
import { FormInput, FormButton } from '../common/FormInput';
import { IProjectScript } from '@packages/global';
import { useTranslation } from 'react-i18next';

interface ChapterFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (title: string) => Promise<void>;
  editingChapter?: IProjectScript | null;
}

export default function ChapterForm({
  isOpen,
  onClose,
  onSubmit,
  editingChapter
}: ChapterFormProps) {
  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(['project', 'common']);

  const isEditing = !!editingChapter;

  useEffect(() => {
    if (editingChapter) {
      setTitle(editingChapter.title);
    } else {
      setTitle('');
    }
    setError('');
  }, [editingChapter, isOpen]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError(t('project:chapter.titleRequired'));
      return;
    }

    setLoading(true);
    try {
      await onSubmit(title.trim());
      onClose();
    } catch (err) {
      console.error('Submit failed:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? t('project:chapter.edit') : t('project:chapter.add')}
      size="sm"
      footer={
        <>
          <FormButton variant="secondary" onClick={onClose}>
            {t('common:cancel')}
          </FormButton>
          <FormButton variant="primary" loading={loading} onClick={handleSubmit}>
            {isEditing ? t('common:save') : t('project:chapter.add')}
          </FormButton>
        </>
      }
    >
      <FormInput
        label={t('project:chapter.titleLabel')}
        placeholder={t('project:chapter.titlePlaceholder')}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        error={error}
        autoFocus
      />
    </FormModal>
  );
}
